// Quy tắc thuần cho hồ sơ 360: gom dữ liệu POS + thẻ liệu trình theo khách, chỉ số, hạng, phân khúc, sổ giao dịch, việc cần làm
import dayjs from 'dayjs';
import { CATALOG, STAFF, type Customer, type Order, type PaymentMethod, type Refund, type Transaction } from '../POS/data';
import type { CardEventKind, TreatmentCard, TreatmentConfig } from '../Treatment/data';
import { cardBalance, cardFlags, cardStatus, installmentRows, outstanding } from '../Treatment/logic';
import {
  SIGN_KIND, type ClassOverride, type Customer360Config, type SegmentKey, type SignDoc, type SignKind, type Tier, type TreatmentPhoto, type VisitRecord, type AuditLog,
} from './data';

const sum = (xs: number[]) => xs.reduce((s, x) => s + x, 0);
const day = (iso: string) => iso.slice(0, 10);

/** Chuẩn hóa SĐT để so khớp: bỏ ký tự lạ, +84/84 → 0 */
export function normPhone(p?: string | null) {
  const d = String(p || '').replace(/\D/g, '');
  if (d.startsWith('84') && d.length === 11) return `0${d.slice(2)}`;
  return d;
}

/** Hồ sơ bán hàng (POS) khớp với khách CMS: liên kết tay trước, rồi theo SĐT chính / phụ */
export function matchPosCustomers(phones: (string | null | undefined)[], posCustomers: Customer[], linkedIds: string[] = []) {
  const set = new Set(phones.map(normPhone).filter(p => p.length >= 9));
  const out = posCustomers.filter(c => linkedIds.includes(c.id) || set.has(normPhone(c.phone)));
  return out;
}

// ---------------------------------------------------------------------------
// Dữ liệu của một khách

export interface CustomerData {
  orders: Order[];
  transactions: Transaction[];
  refunds: Refund[];
  cards: TreatmentCard[];
  docs: SignDoc[];
  photos: TreatmentPhoto[];
}

export function collectCustomerData(
  posIds: string[], customerKey: string,
  src: { orders: Order[]; transactions: Transaction[]; refunds: Refund[]; cards: TreatmentCard[]; docs: SignDoc[]; photos: TreatmentPhoto[] },
): CustomerData {
  const ids = new Set(posIds);
  const orders = src.orders.filter(o => o.customerId && ids.has(o.customerId));
  const orderIds = new Set(orders.map(o => o.id));
  const cards = src.cards.filter(c => ids.has(c.customerId));
  const cardIds = new Set(cards.map(c => c.id));
  const mine = (x: { customerKey?: string; cardId?: string; posCustomerId?: string }) =>
    x.customerKey === customerKey || (!!x.cardId && cardIds.has(x.cardId)) || (!!x.posCustomerId && ids.has(x.posCustomerId));
  return {
    orders,
    transactions: src.transactions.filter(t => orderIds.has(t.orderId)),
    refunds: src.refunds.filter(r => orderIds.has(r.orderId)),
    cards,
    docs: src.docs.filter(mine),
    photos: src.photos.filter(mine),
  };
}

// ---------------------------------------------------------------------------
// Chỉ số

export interface CustomerMetrics {
  /** Thực trả trong kỳ xét hạng (đã trừ hoàn) */
  spendWindow: number;
  /** Thực trả từ trước đến nay */
  lifetimeSpend: number;
  /** Tổng giá trị mua (hóa đơn + thẻ chuyển từ hệ thống cũ) */
  billed: number;
  orderCount: number;
  avgTicket: number;
  /** Ngày có phát sinh mua hoặc trừ buổi */
  visits: number;
  visitsWindow: number;
  firstVisit: string | null;
  lastVisit: string | null;
  daysSinceLast: number | null;
  debt: number;
  overdue: number;
  activeCards: number;
  sessionsLeft: number;
  valueLeft: number;
  usedSessions: number;
}

const nonCancelled = (o: Order) => o.status !== 'cancelled';

export function customerMetrics(d: CustomerData, cfg: Pick<Customer360Config, 'windowMonths'>, tcfg: TreatmentConfig, now = new Date()): CustomerMetrics {
  const from = dayjs(now).subtract(cfg.windowMonths, 'month').toISOString();
  const orders = d.orders.filter(nonCancelled);
  const imported = d.cards.filter(c => c.source === 'import');
  // Tiền thực trả: giao dịch POS (hoàn là số âm) + tiền thẻ cũ thu ngoài POS
  const money = [
    ...d.transactions.map(t => ({ at: t.at, amount: t.amount })),
    ...d.cards.flatMap(c => c.payments.filter(p => !p.posTxnId).map(p => ({ at: p.at, amount: p.amount }))),
    ...d.cards.flatMap(c => c.refunds.filter(r => r.status === 'paid' && !r.posRefundId).map(r => ({ at: r.approvedAt || r.at, amount: -r.amount }))),
  ];
  const visitDays = new Set<string>([
    ...orders.map(o => day(o.createdAt)),
    ...d.cards.flatMap(c => c.usages.filter(u => !u.cancelled).map(u => day(u.at))),
  ]);
  const sortedDays = Array.from(visitDays).sort();
  const lastVisit = sortedDays[sortedDays.length - 1] || null;
  const live = d.cards.filter(c => cardStatus(c, now) === 'active');
  const billed = sum(orders.map(o => o.totals.total)) + sum(imported.filter(c => c.status !== 'cancelled').map(c => c.price));
  return {
    spendWindow: sum(money.filter(m => m.at >= from).map(m => m.amount)),
    lifetimeSpend: sum(money.map(m => m.amount)),
    billed,
    orderCount: orders.length,
    avgTicket: orders.length ? Math.round(sum(orders.map(o => o.totals.total)) / orders.length) : 0,
    visits: visitDays.size,
    visitsWindow: sortedDays.filter(x => x >= day(from)).length,
    firstVisit: sortedDays[0] || null,
    lastVisit,
    daysSinceLast: lastVisit ? dayjs(now).startOf('day').diff(dayjs(lastVisit), 'day') : null,
    debt: sum(orders.map(o => o.debt)) + sum(imported.map(outstanding)),
    overdue: sum(d.cards.map(c => cardFlags(c, tcfg, now).overdueAmount)),
    activeCards: live.length,
    sessionsLeft: sum(live.filter(c => c.kind === 'session').map(c => cardBalance(c).remainingSessions)),
    valueLeft: sum(live.filter(c => c.kind === 'value').map(c => cardBalance(c).remainingValue)),
    usedSessions: sum(d.cards.flatMap(c => c.usages.filter(u => !u.cancelled).flatMap(u => u.items.map(i => i.qty)))),
  };
}

// ---------------------------------------------------------------------------
// Hạng thành viên + phân khúc

export function tierOf(spend: number, rules: Customer360Config['tierRules']): Tier {
  const ok = [...rules].sort((a, b) => a.minSpend - b.minSpend).filter(r => spend >= r.minSpend);
  return ok.length ? ok[ok.length - 1].tier : 'Thường';
}

export function nextTier(spend: number, rules: Customer360Config['tierRules']) {
  const next = [...rules].sort((a, b) => a.minSpend - b.minSpend).find(r => r.minSpend > spend);
  return next ? { tier: next.tier, need: next.minSpend - spend, pct: Math.min(1, spend / next.minSpend) } : null;
}

export interface Rfm { r: number; f: number; m: number }

export function rfmOf(m: CustomerMetrics, cfg: Customer360Config): Rfm {
  const ds = m.daysSinceLast;
  const r = ds === null ? 0 : ds <= 30 ? 5 : ds <= 60 ? 4 : ds <= cfg.atRiskDays ? 3 : ds <= cfg.lostDays ? 2 : 1;
  const v = m.visitsWindow;
  const f = v >= 12 ? 5 : v >= cfg.loyalVisits ? 4 : v >= 3 ? 3 : v >= 2 ? 2 : v >= 1 ? 1 : 0;
  const rule = (t: Tier) => cfg.tierRules.find(x => x.tier === t)?.minSpend ?? Infinity;
  const s = m.spendWindow;
  const mm = s >= rule('Kim cương') ? 5 : s >= rule('Vàng') ? 4 : s >= rule('Bạc') ? 3 : s > 0 ? 2 : 0;
  return { r, f, m: mm };
}

export const SEGMENT: Record<SegmentKey, { label: string; cls: string; advice: string }> = {
  lead: { label: 'Chưa phát sinh mua', cls: 'bg-slate-100 text-slate-600 border-slate-200', advice: 'Tư vấn – mời trải nghiệm dịch vụ đầu tiên' },
  new: { label: 'Khách mới', cls: 'bg-sky-50 text-sky-700 border-sky-200', advice: 'Gọi hỏi thăm sau buổi đầu, giới thiệu gói liệu trình phù hợp' },
  vip: { label: 'VIP', cls: 'bg-amber-50 text-amber-700 border-amber-300', advice: 'Chăm sóc riêng, ưu tiên lịch, mời sự kiện – sản phẩm mới' },
  loyal: { label: 'Trung thành', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', advice: 'Duy trì: nhắc lịch đều, ưu đãi gia hạn thẻ' },
  potential: { label: 'Tiềm năng', cls: 'bg-teal-50 text-teal-700 border-teal-200', advice: 'Gợi ý mua gói / nâng gói để tăng tần suất đến' },
  need_care: { label: 'Cần chăm sóc', cls: 'bg-yellow-50 text-yellow-700 border-yellow-200', advice: 'Lâu chưa quay lại: nhắc lịch buổi tiếp theo' },
  at_risk: { label: 'Có nguy cơ rời bỏ', cls: 'bg-orange-50 text-orange-700 border-orange-200', advice: 'Gọi chăm sóc, tìm lý do, tặng ưu đãi quay lại' },
  lost: { label: 'Ngủ đông', cls: 'bg-red-50 text-red-600 border-red-200', advice: 'Chiến dịch kích hoạt lại (SMS/Zalo), ưu đãi đặc biệt' },
};
export const SEGMENT_KEYS = Object.keys(SEGMENT) as SegmentKey[];

export function segmentOf(m: CustomerMetrics, tier: Tier, cfg: Customer360Config, now = new Date()): SegmentKey {
  if (!m.visits && m.lifetimeSpend <= 0) return 'lead';
  const ds = m.daysSinceLast ?? 0;
  if (ds >= cfg.lostDays) return 'lost';
  if (ds >= cfg.atRiskDays) return 'at_risk';
  if (m.firstVisit && dayjs(now).diff(dayjs(m.firstVisit), 'day') <= cfg.newDays) return 'new';
  const rfm = rfmOf(m, cfg);
  if (tier === 'Kim cương' || (rfm.m >= 4 && rfm.f >= 4)) return 'vip';
  if (m.visitsWindow >= cfg.loyalVisits) return 'loyal';
  if (ds >= cfg.careDays) return 'need_care';
  return 'potential';
}

export interface Classification {
  autoTier: Tier;
  autoSegment: SegmentKey;
  tier: Tier;
  segment: SegmentKey;
  /** Đang áp phân loại tay */
  override: ClassOverride | null;
  rfm: Rfm;
  next: ReturnType<typeof nextTier>;
}

export function classify(m: CustomerMetrics, cfg: Customer360Config, override: ClassOverride | null | undefined, now = new Date()): Classification {
  const autoTier = tierOf(m.spendWindow, cfg.tierRules);
  const autoSegment = segmentOf(m, autoTier, cfg, now);
  const ov = override && (!override.until || override.until >= dayjs(now).format('YYYY-MM-DD')) ? override : null;
  return {
    autoTier, autoSegment,
    tier: ov?.tier || autoTier,
    segment: ov?.segment || autoSegment,
    override: ov,
    rfm: rfmOf(m, cfg),
    next: nextTier(m.spendWindow, cfg.tierRules),
  };
}

/** Nhóm dịch vụ khách quan tâm (theo hóa đơn + thẻ), nhiều nhất trước */
export function interestsOf(d: CustomerData) {
  const count = new Map<string, number>();
  const add = (g: string | undefined, n: number) => { if (g) count.set(g, (count.get(g) || 0) + n); };
  d.orders.filter(nonCancelled).forEach(o => o.lines.forEach(l => add(CATALOG.find(c => c.id === l.itemId)?.group, l.qty)));
  d.cards.forEach(c => add(c.category, 1));
  return Array.from(count.entries()).sort((a, b) => b[1] - a[1]).map(([group, n]) => ({ group, n }));
}

// ---------------------------------------------------------------------------
// Sổ giao dịch hợp nhất

export type LedgerKind = 'order' | 'payment' | 'debt' | 'refund' | 'card_import' | 'card_payment' | 'card_refund' | 'use' | 'extend' | 'adjust';

export const LEDGER_KIND: Record<LedgerKind, { label: string; cls: string; money: boolean }> = {
  order: { label: 'Hóa đơn', cls: 'bg-slate-100 text-slate-700 border-slate-200', money: false },
  payment: { label: 'Thanh toán', cls: 'bg-green-50 text-green-700 border-green-200', money: true },
  debt: { label: 'Thu nợ', cls: 'bg-teal-50 text-teal-700 border-teal-200', money: true },
  refund: { label: 'Hoàn tiền', cls: 'bg-violet-50 text-violet-700 border-violet-200', money: true },
  card_import: { label: 'Thẻ chuyển đổi', cls: 'bg-slate-100 text-slate-700 border-slate-200', money: false },
  card_payment: { label: 'Thu tiền thẻ', cls: 'bg-green-50 text-green-700 border-green-200', money: true },
  card_refund: { label: 'Hoàn tiền thẻ', cls: 'bg-violet-50 text-violet-700 border-violet-200', money: true },
  use: { label: 'Trừ buổi', cls: 'bg-sky-50 text-sky-700 border-sky-200', money: false },
  extend: { label: 'Gia hạn', cls: 'bg-amber-50 text-amber-700 border-amber-200', money: false },
  adjust: { label: 'Điều chỉnh', cls: 'bg-amber-50 text-amber-700 border-amber-200', money: false },
};

export interface LedgerRow {
  id: string;
  at: string;
  kind: LedgerKind;
  /** Mã chứng từ */
  ref: string;
  title: string;
  detail: string;
  branchId: string;
  /** Giá trị chứng từ (hóa đơn, buổi quy tiền…) */
  value: number;
  /** Tiền khách trả (+) / được hoàn (−); 0 nếu không phát sinh tiền */
  cash: number;
  method?: PaymentMethod;
  by: string;
  status?: string;
  link: { type: 'order' | 'card'; id: string };
}

export function buildLedger(d: CustomerData): LedgerRow[] {
  const rows: LedgerRow[] = [];
  d.orders.forEach(o => rows.push({
    id: `o-${o.id}`, at: o.createdAt, kind: 'order', ref: o.id,
    title: o.lines.map(l => `${l.name}${l.qty > 1 ? ` ×${l.qty}` : ''}`).join(', '),
    detail: o.debt > 0 ? `Còn nợ ${new Intl.NumberFormat('vi-VN').format(o.debt)}đ` : o.status === 'cancelled' ? `Đã hủy${o.cancelReason ? `: ${o.cancelReason}` : ''}` : '',
    branchId: o.branchId, value: o.totals.total, cash: 0, by: o.cashier, status: o.status, link: { type: 'order', id: o.id },
  }));
  d.transactions.forEach(t => rows.push({
    id: `t-${t.id}`, at: t.at, kind: t.kind === 'refund' ? 'refund' : t.kind === 'debt_collection' ? 'debt' : 'payment', ref: t.id,
    title: `${t.kind === 'refund' ? 'Hoàn' : 'Thu'} cho đơn ${t.orderId}`,
    detail: t.txnRef ? `Mã GD ${t.txnRef}` : '',
    branchId: t.branchId, value: Math.abs(t.amount), cash: t.amount, method: t.method, by: t.cashier,
    link: { type: 'order', id: t.orderId },
  }));
  d.refunds.filter(r => r.status !== 'paid').forEach(r => rows.push({
    id: `r-${r.id}`, at: r.createdAt, kind: 'refund', ref: r.id, title: `Phiếu hoàn đơn ${r.orderId}`, detail: r.reason,
    branchId: r.branchId, value: r.amount, cash: 0, method: r.method, by: r.createdBy, status: r.status, link: { type: 'order', id: r.orderId },
  }));
  d.cards.forEach(c => {
    const link = { type: 'card' as const, id: c.id };
    if (c.source === 'import') rows.push({ id: `ci-${c.id}`, at: c.createdAt, kind: 'card_import', ref: c.id, title: c.packageName, detail: 'Thẻ chuyển từ hệ thống cũ', branchId: c.branchId, value: c.price, cash: 0, by: c.soldBy, link });
    c.payments.filter(p => !p.posTxnId).forEach(p => rows.push({ id: `cp-${p.id}`, at: p.at, kind: 'card_payment', ref: p.id, title: c.packageName, detail: p.txnRef ? `Mã GD ${p.txnRef}` : '', branchId: p.branchId, value: p.amount, cash: p.amount, method: p.method, by: p.by, link }));
    c.refunds.filter(r => !r.posRefundId).forEach(r => rows.push({ id: `cr-${r.id}`, at: r.at, kind: 'card_refund', ref: r.id, title: c.packageName, detail: r.reason, branchId: c.branchId, value: r.amount, cash: r.status === 'paid' ? -r.amount : 0, method: r.method, by: r.by, status: r.status, link }));
    c.usages.forEach(u => rows.push({
      id: `u-${u.id}`, at: u.at, kind: 'use', ref: u.id, title: c.packageName,
      detail: u.items.map(i => `${i.name} ×${i.qty}`).join(', ') + (u.cancelled ? ` · đã hủy: ${u.cancelled.reason}` : ''),
      branchId: u.branchId, value: sum(u.items.map(i => i.value)), cash: 0, by: u.by, status: u.cancelled ? 'cancelled' : undefined, link,
    }));
    c.events.filter(e => e.kind === 'extend' || e.kind === 'adjust').forEach((e, i) => rows.push({
      id: `e-${c.id}-${i}`, at: e.at, kind: e.kind as LedgerKind, ref: c.id, title: c.packageName, detail: e.text,
      branchId: c.branchId, value: e.amount || 0, cash: 0, by: e.by, link,
    }));
  });
  return rows.sort((a, b) => b.at.localeCompare(a.at));
}

export function ledgerSummary(rows: LedgerRow[]) {
  return {
    billed: sum(rows.filter(r => (r.kind === 'order' && r.status !== 'cancelled') || r.kind === 'card_import').map(r => r.value)),
    paid: sum(rows.filter(r => r.cash > 0).map(r => r.cash)),
    refunded: sum(rows.filter(r => r.cash < 0).map(r => -r.cash)),
    used: sum(rows.filter(r => r.kind === 'use' && r.status !== 'cancelled').map(r => r.value)),
  };
}

// ---------------------------------------------------------------------------
// Lần đến cơ sở: gom lượt trừ buổi + hóa đơn của khách theo ngày, khách ký một phiếu cho cả lần đến

export interface VisitItem {
  /** u:<mã lượt trừ> · o:<mã hóa đơn> */
  key: string;
  kind: 'use' | 'order';
  at: string;
  branchId: string;
  title: string;
  detail: string;
  staff: string[];
  posCustomerId: string;
  cardId?: string;
  usageId?: string;
  orderId?: string;
  /** Có làm dịch vụ → cần khách ký (mua sản phẩm / mua thẻ thì không) */
  needSign: boolean;
}

export type VisitStatus = 'none' | 'signed' | 'paper' | 'partial' | 'pending';

export const VISIT_STATUS: Record<VisitStatus, { label: string; cls: string }> = {
  signed: { label: 'Đã ký', cls: 'bg-green-50 text-green-700 border-green-200' },
  paper: { label: 'Ký giấy', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  partial: { label: 'Cần ký bổ sung', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  pending: { label: 'Chờ ký', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  none: { label: 'Không cần ký', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

export interface Visit {
  date: string;
  firstAt: string;
  lastAt: string;
  branchIds: string[];
  posCustomerIds: string[];
  items: VisitItem[];
  staff: string[];
  /** Phiếu đã ký (còn hiệu lực) phủ lần đến này */
  docs: SignDoc[];
  records: VisitRecord[];
  /** Mục đã có chữ ký điện tử / phiếu giấy */
  signedKeys: string[];
  paperKeys: string[];
  /** Mục cần ký nhưng chưa có chữ ký */
  unsigned: VisitItem[];
  status: VisitStatus;
  /** Chưa ký quá hạn cho phép → báo quản lý */
  overdue: boolean;
  /** Số ngày tính từ lần đến */
  age: number;
  rating?: number;
  ratingNote?: string;
  deferred?: VisitRecord['deferred'];
  paperPhotoId?: string;
  photos: TreatmentPhoto[];
}

const staffNameOf = (id: string) => STAFF.find(s => s.id === id)?.name || id;

export function visitItemsOf(d: CustomerData): VisitItem[] {
  const items: VisitItem[] = [];
  d.cards.forEach(c => {
    c.usages.filter(u => !u.cancelled).forEach(u => {
      // Số dư ngay sau lượt trừ này (không lấy số dư hiện tại)
      const b = cardBalance({ ...c, usages: c.usages.filter(x => !x.cancelled && x.at <= u.at) });
      const left = c.kind === 'session' ? `còn ${b.remainingSessions}/${b.totalSessions} buổi` : `còn ${new Intl.NumberFormat('vi-VN').format(b.remainingValue)}đ`;
      items.push({
        key: `u:${u.id}`, kind: 'use', at: u.at, branchId: u.branchId, posCustomerId: c.customerId, cardId: c.id, usageId: u.id, needSign: true,
        title: c.packageName, detail: `${u.items.map(i => `${i.name} ×${i.qty}`).join(', ')} – ${left}`, staff: [staffNameOf(u.staffId)],
      });
    });
  });
  d.orders.filter(nonCancelled).forEach(o => {
    const services = o.lines.filter(l => l.type === 'service');
    items.push({
      key: `o:${o.id}`, kind: 'order', at: o.createdAt, branchId: o.branchId, posCustomerId: o.customerId || '', orderId: o.id,
      needSign: services.length > 0,
      title: `Hóa đơn ${o.id}`,
      detail: `${o.lines.map(l => `${l.name}${l.qty > 1 ? ` ×${l.qty}` : ''}`).join(', ')} – ${o.debt > 0 ? `còn nợ ${new Intl.NumberFormat('vi-VN').format(o.debt)}đ` : 'đã thanh toán'}`,
      staff: Array.from(new Set(services.map(l => l.staffId).filter(Boolean).map(staffNameOf))),
    });
  });
  return items.sort((a, b) => a.at.localeCompare(b.at));
}

export const visitRecordId = (posCustomerId: string, date: string) => `${posCustomerId}|${date}`;

export function visitsOf(d: CustomerData, records: VisitRecord[], cfg: Pick<Customer360Config, 'visitSignDays' | 'visitSignSince'>, now = new Date()): Visit[] {
  const byDate = new Map<string, VisitItem[]>();
  visitItemsOf(d).forEach(i => {
    const k = dayjs(i.at).format('YYYY-MM-DD');
    byDate.set(k, [...(byDate.get(k) || []), i]);
  });
  const liveDocs = d.docs.filter(x => x.status === 'signed');
  const legacyPaper = new Set(d.cards.flatMap(c => c.usages.filter(u => u.signed).map(u => `u:${u.id}`)));
  const today = dayjs(now).startOf('day');
  return Array.from(byDate.entries()).map(([date, items]) => {
    const posIds = Array.from(new Set(items.map(i => i.posCustomerId)));
    const recs = records.filter(r => r.date === date && posIds.includes(r.posCustomerId));
    const keys = new Set(items.map(i => i.key));
    const docs = liveDocs.filter(x => (x.kind === 'visit' && (x.coveredKeys || []).some(k => keys.has(k))) || (x.kind === 'session' && !!x.usageId && keys.has(`u:${x.usageId}`)));
    const signed = new Set(docs.flatMap(x => (x.kind === 'visit' ? x.coveredKeys || [] : [`u:${x.usageId}`])));
    const paper = new Set([...recs.flatMap(r => r.paper?.keys || []), ...Array.from(legacyPaper).filter(k => keys.has(k))].filter(k => !signed.has(k)));
    // Lần đến trước ngày áp dụng ký điện tử: không đòi chữ ký
    const need = cfg.visitSignSince && date < cfg.visitSignSince ? [] : items.filter(i => i.needSign);
    const unsigned = need.filter(i => !signed.has(i.key) && !paper.has(i.key));
    const status: VisitStatus = !need.length ? 'none'
      : !unsigned.length ? (need.some(i => paper.has(i.key)) ? 'paper' : 'signed')
        : unsigned.length < need.length ? 'partial' : 'pending';
    const age = today.diff(dayjs(date), 'day');
    const rated = recs.find(r => r.rating);
    return {
      date, items, posCustomerIds: posIds,
      firstAt: items[0].at, lastAt: items[items.length - 1].at,
      branchIds: Array.from(new Set(items.map(i => i.branchId))),
      staff: Array.from(new Set(items.flatMap(i => i.staff))),
      docs, records: recs,
      signedKeys: Array.from(signed).filter(k => keys.has(k)), paperKeys: Array.from(paper),
      unsigned, status, age,
      overdue: (status === 'pending' || status === 'partial') && age > cfg.visitSignDays,
      rating: rated?.rating, ratingNote: rated?.ratingNote,
      deferred: recs.find(r => r.deferred)?.deferred,
      paperPhotoId: recs.find(r => r.paper?.photoId)?.paper?.photoId,
      photos: d.photos.filter(p => !p.hidden && (dayjs(p.takenAt).format('YYYY-MM-DD') === date || (!!p.usageId && keys.has(`u:${p.usageId}`)))),
    };
  }).sort((a, b) => b.date.localeCompare(a.date));
}

// ---------------------------------------------------------------------------
// Văn bản cần ký

export interface SignNeed {
  key: string;
  kind: SignKind;
  cardId?: string;
  usageId?: string;
  /** Lần đến cần ký xác nhận */
  visitDate?: string;
  overdue?: boolean;
  title: string;
  hint: string;
}

export function signNeeds(d: CustomerData, visits: Visit[] = []): SignNeed[] {
  const signed = d.docs.filter(x => x.status === 'signed');
  const has = (kind: SignKind, f: (x: SignDoc) => boolean = () => true) => signed.some(x => x.kind === kind && f(x));
  const out: SignNeed[] = [];
  const liveCards = d.cards.filter(c => c.status !== 'cancelled');
  if (liveCards.length && !has('consent')) out.push({ key: 'consent', kind: 'consent', title: SIGN_KIND.consent.label, hint: 'Ký trước buổi thực hiện đầu tiên' });
  liveCards.forEach(c => {
    if (!has('contract', x => x.cardId === c.id)) out.push({ key: `contract-${c.id}`, kind: 'contract', cardId: c.id, title: `${SIGN_KIND.contract.short} · ${c.packageName}`, hint: `Thẻ ${c.id}` });
  });
  const shownPhotos = d.photos.filter(p => !p.hidden);
  if (shownPhotos.length && !has('photo')) out.push({ key: 'photo', kind: 'photo', title: SIGN_KIND.photo.label, hint: `Đã lưu ${shownPhotos.length} ảnh nhưng khách chưa ký đồng ý` });
  visits.filter(v => v.status === 'pending' || v.status === 'partial').forEach(v => out.push({
    key: `visit-${v.date}`, kind: 'visit', visitDate: v.date, overdue: v.overdue,
    title: `${v.status === 'partial' ? 'Ký bổ sung' : SIGN_KIND.visit.short} ngày ${dayjs(v.date).format('DD/MM/YYYY')}`,
    hint: `${v.unsigned.map(i => i.kind === 'use' ? `${i.title}: ${i.detail.split(' – ')[0]}` : i.detail.split(' – ')[0]).join(' · ')}${v.overdue ? ` · quá ${v.age} ngày chưa ký` : ''}${v.deferred ? ` · hẹn ký sau: ${v.deferred.reason}` : ''}`,
  }));
  return out;
}

// ---------------------------------------------------------------------------
// Hành trình + việc nên làm

export interface JourneyItem { at: string; kind: 'profile' | 'order' | 'card' | 'use' | 'pay' | 'sign' | 'photo' | 'refund'; title: string; detail?: string }

export function journeyOf(d: CustomerData, createdAt?: string | null): JourneyItem[] {
  const out: JourneyItem[] = [];
  if (createdAt) out.push({ at: createdAt, kind: 'profile', title: 'Tạo hồ sơ khách hàng' });
  d.orders.forEach(o => out.push({ at: o.createdAt, kind: 'order', title: `Mua hàng ${o.id}`, detail: o.lines.map(l => l.name).join(', ') }));
  d.cards.forEach(c => {
    out.push({ at: c.createdAt, kind: 'card', title: `Mở thẻ ${c.packageName}`, detail: c.id });
    c.usages.filter(u => !u.cancelled).forEach(u => out.push({ at: u.at, kind: 'use', title: 'Đến làm dịch vụ', detail: `${c.packageName}: ${u.items.map(i => `${i.name} ×${i.qty}`).join(', ')}` }));
    c.refunds.forEach(r => out.push({ at: r.at, kind: 'refund', title: 'Hoàn tiền thẻ', detail: `${c.packageName} · ${r.reason}` }));
  });
  d.docs.forEach(x => out.push({ at: x.signedAt, kind: 'sign', title: `Ký ${SIGN_KIND[x.kind].short}`, detail: x.title }));
  const photoDays = new Map<string, number>();
  d.photos.filter(p => !p.hidden).forEach(p => photoDays.set(day(p.takenAt), (photoDays.get(day(p.takenAt)) || 0) + 1));
  photoDays.forEach((n, dd) => out.push({ at: `${dd}T23:59:00`, kind: 'photo', title: `Lưu ${n} ảnh liệu trình` }));
  return out.sort((a, b) => b.at.localeCompare(a.at));
}

export type ActionKind = 'pay' | 'book' | 'sign' | 'visit' | 'photo' | 'renew' | 'care' | 'tier';
export interface NextAction { key: string; kind: ActionKind; tone: 'danger' | 'warn' | 'info'; title: string; detail: string; cardId?: string; visitDate?: string }

export function nextActions(d: CustomerData, m: CustomerMetrics, cls: Classification, tcfg: TreatmentConfig, needs: SignNeed[], now = new Date()): NextAction[] {
  const out: NextAction[] = [];
  d.cards.forEach(c => {
    const st = cardStatus(c, now);
    const rows = installmentRows(c, tcfg, now);
    const od = rows.find(r => r.state === 'overdue');
    const ds = rows.find(r => r.state === 'due_soon');
    if (od) out.push({ key: `od-${c.id}`, kind: 'pay', tone: 'danger', cardId: c.id, title: `Thu đợt ${od.no} quá hạn ${od.overdueDays} ngày`, detail: `${c.packageName} · còn thiếu ${new Intl.NumberFormat('vi-VN').format(od.left)}đ` });
    else if (ds) out.push({ key: `ds-${c.id}`, kind: 'pay', tone: 'warn', cardId: c.id, title: `Nhắc đóng đợt ${ds.no} hạn ${dayjs(ds.dueDate).format('DD/MM')}`, detail: `${c.packageName} · ${new Intl.NumberFormat('vi-VN').format(ds.left)}đ` });
    if (st !== 'active') return;
    const f = cardFlags(c, tcfg, now);
    if (f.low || f.expiring) out.push({ key: `rn-${c.id}`, kind: 'renew', tone: 'warn', cardId: c.id, title: f.expiring ? `Thẻ hết hạn sau ${Math.max(0, f.daysToExpire)} ngày` : 'Thẻ sắp dùng hết', detail: `${c.packageName} · tư vấn ${f.expiring ? 'gia hạn' : 'mua thêm / nâng gói'}` });
    const lastUse = c.usages.filter(u => !u.cancelled).map(u => u.at).sort().pop();
    const booked = c.bookings.some(b => b.status === 'booked' && b.at >= now.toISOString());
    const idle = dayjs(now).diff(dayjs(lastUse || c.createdAt), 'day');
    if (!booked && idle >= 21 && cardBalance(c).pctLeft > 0) out.push({ key: `bk-${c.id}`, kind: 'book', tone: 'info', cardId: c.id, title: 'Đặt lịch buổi tiếp theo', detail: `${c.packageName} · ${lastUse ? `${idle} ngày chưa đến` : 'chưa dùng buổi nào'}` });
    const photos = d.photos.filter(p => p.cardId === c.id && !p.hidden);
    if (c.kind === 'session' && !photos.some(p => p.phase === 'before')) out.push({ key: `ph-${c.id}`, kind: 'photo', tone: 'info', cardId: c.id, title: 'Chụp ảnh trước liệu trình', detail: `${c.packageName} · chưa có ảnh "Trước" để so sánh kết quả` });
  });
  // Lần đến chưa ký: hôm nay → nhắc hoàn tất; quá hạn → báo đỏ
  const today = dayjs(now).format('YYYY-MM-DD');
  needs.filter(n => n.kind === 'visit').forEach(n => out.push({
    key: n.key, kind: 'visit', visitDate: n.visitDate, tone: n.overdue ? 'danger' : n.visitDate === today ? 'warn' : 'info',
    title: n.visitDate === today ? 'Hoàn tất lần đến hôm nay – khách chưa ký xác nhận' : n.title, detail: n.hint,
  }));
  const docs = needs.filter(n => n.kind !== 'visit');
  if (docs.length) out.push({ key: 'sign', kind: 'sign', tone: 'warn', title: `${docs.length} văn bản chờ khách ký`, detail: docs.slice(0, 2).map(n => n.title).join(' · ') + (docs.length > 2 ? '…' : '') });
  if (cls.segment === 'at_risk' || cls.segment === 'lost' || cls.segment === 'need_care') out.push({ key: 'care', kind: 'care', tone: cls.segment === 'need_care' ? 'info' : 'warn', title: SEGMENT[cls.segment].label, detail: `${m.daysSinceLast} ngày chưa quay lại · ${SEGMENT[cls.segment].advice}` });
  if (cls.next && cls.next.pct >= 0.8) out.push({ key: 'tier', kind: 'tier', tone: 'info', title: `Còn ${new Intl.NumberFormat('vi-VN').format(cls.next.need)}đ để lên hạng ${cls.next.tier}`, detail: 'Gợi ý gói phù hợp để khách đạt hạng' });
  const rank = { danger: 0, warn: 1, info: 2 };
  return out.sort((a, b) => rank[a.tone] - rank[b.tone]);
}

// ---------------------------------------------------------------------------
// Lịch sử cập nhật suy ra từ dữ liệu có sẵn (thẻ, chữ ký, ảnh, lần đến) — gộp cùng nhật ký sửa hồ sơ

const CARD_EVENT: Record<CardEventKind, string> = {
  issue: 'Phát hành thẻ', payment: 'Thu tiền thẻ', use: 'Trừ buổi', cancel_use: 'Hủy lượt trừ buổi', extend: 'Gia hạn thẻ', adjust: 'Điều chỉnh thẻ',
  booking: 'Lịch hẹn từ thẻ', refund: 'Hoàn tiền thẻ', plan: 'Đổi lịch thanh toán', print: 'In phiếu',
};

export function derivedHistory(d: CustomerData, visits: Visit[]): AuditLog[] {
  const out: AuditLog[] = [];
  d.cards.forEach(c => c.events.forEach((e, i) => out.push({
    id: `ce-${c.id}-${i}`, at: e.at, by: e.by, kind: 'treatment', title: `${CARD_EVENT[e.kind] || e.kind} · ${c.packageName}`, note: e.text,
  })));
  d.docs.forEach(x => {
    out.push({ id: `ds-${x.id}`, at: x.signedAt, by: x.witness, kind: 'sign', title: `Khách ký ${SIGN_KIND[x.kind].short}`, note: `${x.id} · ${x.title}` });
    if (x.revoked) out.push({ id: `dr-${x.id}`, at: x.revoked.at, by: x.revoked.by, kind: 'sign', title: `Thu hồi văn bản ${x.id}`, note: x.revoked.reason });
  });
  const shots = new Map<string, { at: string; by: string; n: number }>();
  d.photos.forEach(p => {
    const k = `${p.createdAt.slice(0, 10)}|${p.by}`;
    const cur = shots.get(k) || { at: p.createdAt, by: p.by, n: 0 };
    shots.set(k, { ...cur, n: cur.n + 1 });
    if (p.hidden) out.push({ id: `ph-${p.id}`, at: p.hidden.at, by: p.hidden.by, kind: 'photo', title: `Ẩn ảnh ${p.id}`, note: p.hidden.reason });
  });
  shots.forEach((v, k) => out.push({ id: `pa-${k}`, at: v.at, by: v.by, kind: 'photo', title: `Thêm ${v.n} ảnh liệu trình` }));
  visits.forEach(v => v.records.forEach(r => {
    if (r.paper) out.push({ id: `vp-${r.id}`, at: r.paper.at, by: r.paper.by, kind: 'visit', title: `Ghi nhận khách ký phiếu giấy · lần đến ${dayjs(v.date).format('DD/MM/YYYY')}`, note: r.paper.photoId ? 'Có ảnh chụp phiếu' : undefined });
    if (r.deferred) out.push({ id: `vd-${r.id}`, at: r.deferred.at, by: r.deferred.by, kind: 'visit', title: `Hẹn ký sau · lần đến ${dayjs(v.date).format('DD/MM/YYYY')}`, note: r.deferred.reason });
  }));
  return out;
}
